import { Skeleton } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { ErrorScreen } from 'components/ErrorScreen'
import type { UnitComponentTabItem } from 'features/libra/components/UnitComponentTabs'
import { UnitComponentTabs } from 'features/libra/components/UnitComponentTabs'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import {
  buildInspectionsByComponentId,
  sortComponentsForTabs,
} from 'features/libra/ticket/selectComponentInspection'
import { FC, ReactNode, useMemo } from 'react'

/**
 * Wraps a component-scoped step's workspace in per-component tabs driven by
 * the unit's LIVE component list: tabs always reflect the unit's current
 * components, and each tab's inspection is resolved at render time by
 * `buildInspectionsByComponentId` (see `selectComponentInspection.ts`) — the
 * most-recent inspection that actually has CDF data, falling back to
 * most-recent by date when none of a component's inspections carry data. This
 * is NOT persisted on the ticket. Deterministic tab ordering comes from
 * `sortComponentsForTabs` (data.name codepoint compare with id tiebreak).
 *
 * Owns every empty/loading/error state for the tab strip -- by the time
 * `UnitComponentTabs` mounts, there is at least one tab.
 */
export const TabbedWorkspace: FC<{
  children: ReactNode
}> = ({ children }) => {
  const {
    components: allUnitComponents,
    inspectionEntries,
    // Both feed the data-aware inspection pick below (CDS resolves the CDF
    // `component-dataset` bridge).
    componentDataFrames,
    componentDatasets,
    isLoadingComponentsAndInspections,
    componentsAndInspectionsError,
    forceRefresh,
  } = useLibraAssetModelData()

  const sortedComponents = useMemo(
    () => sortComponentsForTabs(allUnitComponents),
    [allUnitComponents]
  )
  const inspectionsByComponentId = useMemo(
    () =>
      buildInspectionsByComponentId(
        sortedComponents,
        inspectionEntries,
        componentDataFrames,
        componentDatasets
      ),
    [
      sortedComponents,
      inspectionEntries,
      componentDataFrames,
      componentDatasets,
    ]
  )
  const componentTabs = useMemo<ReadonlyArray<UnitComponentTabItem>>(() => {
    // Only components with an inspection get a tab (downstream FFS
    // validators error on missing `inspectionId`).
    return sortedComponents
      .filter((c) => c.id in inspectionsByComponentId)
      .map((c) => ({ id: c.id, name: c.data.name || c.id }))
  }, [sortedComponents, inspectionsByComponentId])

  if (isLoadingComponentsAndInspections) {
    return <Skeleton active paragraph={{ rows: 10 }} />
  }
  if (componentsAndInspectionsError) {
    return (
      <ErrorScreen
        title='Failed to load unit data'
        message={`Could not load this unit's components and inspections from Rolodex: ${componentsAndInspectionsError.message}`}>
        <CantileverButton onClick={forceRefresh}>Retry</CantileverButton>
      </ErrorScreen>
    )
  }
  if (componentTabs.length === 0) {
    return (
      <ErrorScreen
        title='No components with inspections'
        message='This unit has no components with inspections. Add a component and upload an inspection in Rolodex before opening this ticket.'
      />
    )
  }
  return (
    <UnitComponentTabs
      components={componentTabs}
      inspectionsByComponentId={inspectionsByComponentId}>
      {children}
    </UnitComponentTabs>
  )
}
